import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { ILanguage } from './core/interfaces/ILanguage';
import { ResolverKeys } from './core/enums/ResolverKeys';
import { authGuard } from './features/auth/core/guards/auth.guard';
import { noAuthGuard } from './features/auth/core/guards/no-auth.guard';
import { myEmployeeResolver } from './features/employees/core/resolvers/my-employee.resolver';
import { terminalResolver } from './features/terminals/core/resolvers/terminal.resolver';
import { terminalGuard } from './features/terminals/core/guards/terminal.guard';
import { showsResolver } from './features/shows/core/resolvers/shows.resolver';
import { kioskTabsResolver } from './features/kiosk/core/resolvers/kiosk-tabs.resolver';
import { kioskProductsAndPackagesResolver } from './features/kiosk/core/resolvers/kiosk-products-and-package.resolver';
import { weatherResolver } from './features/dashboard/core/resolvers/weather.resolver';
import { vouchersResolver } from './features/vouchers/core/resolvers/vouchers.resolver';
import {
  cardsResolver,
  issuersResolver,
  productGroupsResolver,
  usableCardsResolver
} from './features/cards/core/resolvers/cards.resolver';
import { externalVouchersResolver } from './features/vouchers/core/resolvers/external-vouchers.resolver';
import { sessionStatisticsResolver } from './features/statistics/core/resolvers/session-statistics.resolver';
import { datevAccountsResolver } from './features/datev/core/resolvers/datev-accounts.resolver';
import { inject } from '@angular/core';
import { ToolsService } from './core/services/ToolsService';
import { Dictionaries } from './core/enums/Dictionaries';

const userLang = JSON.parse(localStorage.getItem('user-lang')) as ILanguage;

export const routes: Routes = [
  {
    path: '',
    redirectTo: `${userLang?.key ?? environment.fallbackLanguage}/dashboard`,
    pathMatch: 'full',
  },
  {
    path: ':language',
    children: [
      {
        path: 'auth',
        loadChildren: () => import('./features/auth/auth.routes').then(r => r.authRoutes),
        canActivate: [noAuthGuard]
      },
      {
        path: '',
        loadComponent: () => import('./core/layout/layouts/main-layout/main-layout.component').then(l => l.MainLayoutComponent),
        canActivate: [authGuard, terminalGuard],
        resolve: {
          [ResolverKeys.SHOWS]: showsResolver,
          [ResolverKeys.COUNTRIES]: () => inject(ToolsService).getDictionary(Dictionaries.COUNTRIES),
          [ResolverKeys.LANGUAGES]: () => inject(ToolsService).getDictionary(Dictionaries.LANGUAGE),
          [ResolverKeys.CURRENCIES]: () => inject(ToolsService).getCurrencies(),
          [ResolverKeys.VOUCHERS]: vouchersResolver,
          [ResolverKeys.EXTERNAL_VOUCHERS]: externalVouchersResolver,
          [ResolverKeys.CARDS]: cardsResolver,
          [ResolverKeys.USABLE_CARDS]: usableCardsResolver,
          [ResolverKeys.ISSUERS]: issuersResolver,
          [ResolverKeys.PRODUCT_GROUPS]: productGroupsResolver,
          [ResolverKeys.DATEV_ACCOUNTS]: datevAccountsResolver,
          [ResolverKeys.KIOSK_TABS]: kioskTabsResolver,
          [ResolverKeys.KIOSK_PRODUCTS_AND_PACKAGES]: kioskProductsAndPackagesResolver,
        },
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            loadChildren: () => import('./features/dashboard/dashboard.routes').then(r => r.dashboardRoutes),
            resolve: {
              [ResolverKeys.WEATHER]: weatherResolver,
              [ResolverKeys.SESSION_STATISTICS]: sessionStatisticsResolver,
            }
          },
          {
            path: 'shows',
            loadChildren: () => import('./features/shows/shows.routes').then(r => r.showsRoutes)
          }
        ]
      },
      {
        path: 'ots-staff',
        loadChildren: () => import('./features/ots-staff/ots-staff.routes').then(r => r.otsStaffRoutes)
      },
      {
        path: 'blank-screen',
        loadChildren: () => import('./features/blank-screen/blank-screen.routes').then(r => r.blankScreenRoutes)
      },
      {
        path: 'logas/:token',
        loadComponent: () => import('./features/support-mode/pages/logas-page/logas-page.component').then(c => c.LogasPageComponent)
      },
    ],
    resolve: {
      [ResolverKeys.TERMINAL]: terminalResolver,
      [ResolverKeys.MY_EMPLOYEE]: myEmployeeResolver,
    },
  },
];
